import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Filter, X, User, Tag, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger } from
"@/shared/components/ui/popover";
import { Checkbox } from '@/shared/components/ui/checkbox';
import { cn } from '@/shared/utils';

export default function CardFilter({
  members = [],
  labels = [],
  filters,
  onFilterChange,
  className
}) {const { t } = useTranslation("eztask");
  const [showFilters, setShowFilters] = useState(false);

  const activeFilterCount =
  (filters.memberIds?.length || 0) + (
  filters.labelIds?.length || 0) + (
  filters.dueDate ? 1 : 0) + (
  filters.priority ? 1 : 0);

  const handleMemberToggle = (memberId) => {
    const current = filters.memberIds || [];
    const updated = current.includes(memberId) ?
    current.filter((id) => id !== memberId) :
    [...current, memberId];
    onFilterChange({ ...filters, memberIds: updated });
  };

  const handleLabelToggle = (labelId) => {
    const current = filters.labelIds || [];
    const updated = current.includes(labelId) ?
    current.filter((id) => id !== labelId) :
    [...current, labelId];
    onFilterChange({ ...filters, labelIds: updated });
  };

  const handleDueDateChange = (value) => {
    onFilterChange({ ...filters, dueDate: value === filters.dueDate ? null : value });
  };

  const handlePriorityChange = (value) => {
    onFilterChange({ ...filters, priority: value === filters.priority ? null : value });
  };

  const clearFilters = () => {
    onFilterChange({ memberIds: [], labelIds: [], dueDate: null, priority: null });
  };

  const dueDateOptions = [
  { value: 'overdue', label: 'Overdue', color: 'text-red-400' },
  { value: 'today', label: 'Due Today', color: 'text-amber-400' },
  { value: 'week', label: 'Due This Week', color: 'text-blue-400' },
  { value: 'no_date', label: 'No Due Date', color: 'text-slate-400' }];


  const priorityOptions = [
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'low', label: 'Low', color: 'bg-blue-500' }];


  return (
    <div className={cn("flex items-center gap-2", className)} data-testid="card-filter">
      <Popover open={showFilters} onOpenChange={setShowFilters}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "text-white hover:bg-white/20 gap-2",
              activeFilterCount > 0 && "bg-white/20"
            )}
            data-testid="filter-toggle-btn">
            
            <Filter className="w-4 h-4" />{t("filter", "Filter")}

            {activeFilterCount > 0 &&
            <Badge className="bg-blue-600 text-white text-xs px-1.5 h-5">
                {activeFilterCount}
              </Badge>
            }
            <ChevronDown className="w-3 h-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 bg-slate-800 border-slate-700 p-0"
          align="start"
          data-testid="filter-popover">
          
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-white">{t("filterCards", "Filter Cards")}</h3>
              {activeFilterCount > 0 &&
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs text-slate-400 hover:text-white h-7"
                data-testid="clear-filters-btn">{t("clearAll", "Clear all")}


              </Button>
              }
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {/* Members Filter */}
            {members.length > 0 &&
            <div className="p-4 border-b border-slate-700">
                <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-300">
                  <User className="w-4 h-4" />{t("members", "Members")}

              </div>
                <div className="space-y-2">
                  {members.map((member) =>
                <label
                  key={member.id || member.user_id}
                  className="flex items-center gap-3 cursor-pointer hover:bg-slate-700/50 p-2 rounded"
                  data-testid={`filter-member-${member.id || member.user_id}`}>
                  
                      <Checkbox
                    checked={filters.memberIds?.includes(member.id || member.user_id)}
                    onCheckedChange={() => handleMemberToggle(member.id || member.user_id)}
                    className="border-slate-500" />
                  
                      <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium">
                        {member.user_name?.charAt(0).toUpperCase() || member.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <span className="text-sm text-white">
                        {member.user_name || member.name}
                      </span>
                    </label>
                )}
                </div>
              </div>
            }

            {/* Labels Filter */}
            {labels.length > 0 &&
            <div className="p-4 border-b border-slate-700">
                <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-300">
                  <Tag className="w-4 h-4" />{t("labels", "Labels")}

              </div>
                <div className="space-y-2">
                  {labels.map((label) =>
                <label
                  key={label.label_id || label.id}
                  className="flex items-center gap-3 cursor-pointer hover:bg-slate-700/50 p-2 rounded"
                  data-testid={`filter-label-${label.label_id || label.id}`}>
                  
                      <Checkbox
                    checked={filters.labelIds?.includes(label.label_id || label.id)}
                    onCheckedChange={() => handleLabelToggle(label.label_id || label.id)}
                    className="border-slate-500" />
                  
                      <span
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{ backgroundColor: label.color || '#718096' }}>
                    
                        {label.name}
                      </span>
                    </label>
                )}
                </div>
              </div>
            }

            {/* Due Date Filter */}
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-300">
                <Calendar className="w-4 h-4" />{t("dueDate", "Due Date")}

              </div>
              <div className="space-y-2">
                {dueDateOptions.map((option) =>
                <label
                  key={option.value}
                  className="flex items-center gap-3 cursor-pointer hover:bg-slate-700/50 p-2 rounded"
                  data-testid={`filter-due-${option.value}`}>
                  
                    <Checkbox
                    checked={filters.dueDate === option.value}
                    onCheckedChange={() => handleDueDateChange(option.value)}
                    className="border-slate-500" />
                  
                    <span className={cn("text-sm", option.color)}>
                      {option.label}
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Priority Filter */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-300">{t("priority", "Priority")}

              </div>
              <div className="flex flex-wrap gap-2">
                {priorityOptions.map((option) =>
                <button
                  key={option.value}
                  onClick={() => handlePriorityChange(option.value)}
                  className={cn(
                    "px-3 py-1.5 rounded text-xs font-medium transition-all",
                    filters.priority === option.value ?
                    `${option.color} text-white` :
                    "bg-slate-700 text-slate-300 hover:bg-slate-600"
                  )}
                  data-testid={`filter-priority-${option.value}`}>
                  
                    {option.label}
                  </button>
                )}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Active Filter Pills */}
      {activeFilterCount > 0 &&
      <div className="flex items-center gap-2 flex-wrap">
          {filters.memberIds?.map((memberId) => {
          const member = members.find((m) => (m.id || m.user_id) === memberId);
          return member ?
          <Badge
            key={memberId}
            className="bg-blue-900/50 text-blue-300 border border-blue-700 gap-1 cursor-pointer hover:bg-blue-800/50"
            onClick={() => handleMemberToggle(memberId)}>
            
                <User className="w-3 h-3" />
                {member.user_name || member.name}
                <X className="w-3 h-3" />
              </Badge> :
          null;
        })}
          
          {filters.labelIds?.map((labelId) => {
          const label = labels.find((l) => (l.label_id || l.id) === labelId);
          return label ?
          <Badge
            key={labelId}
            className="border gap-1 cursor-pointer"
            style={{ backgroundColor: label.color || '#718096', borderColor: label.color }}
            onClick={() => handleLabelToggle(labelId)}>
            
                {label.name}
                <X className="w-3 h-3" />
              </Badge> :
          null;
        })}

          {filters.dueDate &&
        <Badge
          className="bg-slate-700 text-slate-300 border border-slate-600 gap-1 cursor-pointer hover:bg-slate-600"
          onClick={() => handleDueDateChange(filters.dueDate)}>
          
              <Calendar className="w-3 h-3" />
              {dueDateOptions.find((o) => o.value === filters.dueDate)?.label}
              <X className="w-3 h-3" />
            </Badge>
        }

          {filters.priority &&
        <Badge
          className={cn(
            "gap-1 cursor-pointer",
            priorityOptions.find((o) => o.value === filters.priority)?.color
          )}
          onClick={() => handlePriorityChange(filters.priority)}>
          
              {priorityOptions.find((o) => o.value === filters.priority)?.label}
              <X className="w-3 h-3" />
            </Badge>
        }
        </div>
      }
    </div>);

}

// Helper function to filter cards based on filters
export const filterCards = (cards, filters) => {
  if (!cards) return [];

  return cards.filter((card) => {
    // Filter by members
    if (filters.memberIds?.length > 0) {
      const cardMemberIds = card.members?.map((m) => m.id || m.user_id) || [];
      if (!filters.memberIds.some((id) => cardMemberIds.includes(id))) {
        return false;
      }
    }

    // Filter by labels
    if (filters.labelIds?.length > 0) {
      const cardLabelIds = card.labels?.map((l) => l.label_id || l.id) || [];
      if (!filters.labelIds.some((id) => cardLabelIds.includes(id))) {
        return false;
      }
    }

    // Filter by due date
    if (filters.dueDate) {
      const dueDate = card.due_date ? new Date(card.due_date) : null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + 7);

      switch (filters.dueDate) {
        case 'overdue':
          if (!dueDate || dueDate >= today) return false;
          break;
        case 'today':
          if (!dueDate) return false;
          const dueDateOnly = new Date(dueDate);
          dueDateOnly.setHours(0, 0, 0, 0);
          if (dueDateOnly.getTime() !== today.getTime()) return false;
          break;
        case 'week':
          if (!dueDate || dueDate < today || dueDate > endOfWeek) return false;
          break;
        case 'no_date':
          if (dueDate) return false;
          break;
        default:
          break;
      }
    }

    // Filter by priority
    if (filters.priority) {
      if (card.priority !== filters.priority) return false;
    }

    return true;
  });
};